import type { ChangeEventHandler, ComponentType } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FanResponseDto } from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import isEmpty from 'lodash/isEmpty'
import times from 'lodash/times'
import type { InfiniteData } from 'react-query'
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query'
import type { GridChildComponentProps, GridOnScrollProps } from 'react-window'
import { VariableSizeGrid } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import SearchIcon from '@/assets/icons/icon-search.svg'
import NotFoundImage from '@/assets/images/not-found.svg'
import { Button } from '@/components/ui/button'
import { TextField } from '@/components/ui/text-field'
import { $creatorId } from '@/models/creators.ts'
import ChevronDownIcon from '@/modules/chatting/icons/chevron-down.svg'
import ReloadIcon from '@/modules/chatting/icons/reload.svg'
import { listsService } from '@/modules/chatting/service/lists'
import type {
  GetListUsersResponse,
  GetListsResponse,
  GetUsersListFilters,
  ListUser,
} from '@/modules/chatting/types'
import { unescapeHtmlText } from '@/modules/chatting/ui/chat-window/fan-info/utils'
import { ManageSelectedUsers } from '@/modules/chatting/ui/components/ManageSelectedUsers.tsx'
import { UsersListFilters } from '@/modules/chatting/ui/components/UsersListFilters/UsersListFilters'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import { useCollectionContext } from '../../CollectionProvider'
import type { CollectionsItem } from '../../CollectionsList/types'
import { UserCard } from './UserCard'
import { UserCardSkeleton } from './UserCardSkeleton'

interface Props {
  list: CollectionsItem
  onFinish: () => void
}

export const AddUsers = ({ list, onFinish }: Props) => {
  const [parentRef, width, height] = useSizeObserver()
  const lastVisibleIndexRef = useRef<number | null>(null)
  const [selectedUsers, setSelectedUsers] = useState<ListUser[]>([])
  const creatorId = useUnit($creatorId)
  const { usersQueryKey } = useCollectionContext()
  const queryClient = useQueryClient()

  const addUsersMutation = useMutation(
    (ids: FanResponseDto['id'][]) =>
      listsService.addUsers({ creatorId: creatorId!, listId: list.id, ids }),
    {
      onSuccess: () => {
        queryClient.setQueryData<
          InfiniteData<GetListUsersResponse> | undefined
        >(usersQueryKey, (data) => {
          if (!data) return

          return {
            ...data,
            pages: data.pages.map((page, index, array) => {
              if (index !== array.length - 1) return page

              return {
                ...page,
                list: page.list.concat(selectedUsers),
              }
            }),
          }
        })
        queryClient.setQueriesData<InfiniteData<GetListsResponse> | undefined>(
          'all-lists-extended',
          (oldData) => {
            if (!oldData) return

            return {
              ...oldData,
              pages: oldData.pages.map((page) => {
                return {
                  ...page,
                  list: page.list.map((l) => {
                    if (l.id === list.id) {
                      return {
                        ...l,
                        usersCount: l.usersCount + selectedUsers.length,
                      }
                    }

                    return l
                  }),
                }
              }),
            }
          },
        )
        setSelectedUsers([])
        onFinish()
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while adding users')
      },
    },
  )

  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState<GetUsersListFilters>({})

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading: isLoadingBase,
    hasNextPage,
    isError,
    refetch,
  } = useInfiniteQuery(
    ['subscribers', creatorId, query, JSON.stringify(filters)],
    ({ pageParam = 0 }) =>
      listsService.getSubscribers({
        creatorId: creatorId!,
        offset: pageParam,
        query,
        filters,
        type: 'all',
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.hasMore) {
          return allPages.length * 10
        }
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while getting users')
      },
    },
  )

  const handleAdd = () =>
    addUsersMutation.mutate(selectedUsers.map((u) => u.id))

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = debounce(
    (event) => {
      setQuery(event.target.value)
    },
    500,
  )

  const subscribers = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.list)
        .filter((user) => {
          if (!isTruthyAndNotEmpty(user)) return false

          return !user.listsStates?.some((l) => l.id === list.id)
        }) ?? [],
    [data?.pages, list.id],
  )

  const isLoading = isLoadingBase || (!subscribers.length && isFetchingNextPage)
  const canLoadMore = hasNextPage && !isFetchingNextPage
  const isLoadingMore = isFetchingNextPage && subscribers.length > 0
  const isLastPageFailed = subscribers.length > 0 && isError

  useEffect(() => {
    if (canLoadMore && !subscribers.length) {
      fetchNextPage()
    }
  }, [subscribers, canLoadMore, fetchNextPage])

  const handleScroll = debounce(
    ({ verticalScrollDirection }: GridOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== subscribers.length - 1 ||
        !canLoadMore ||
        isLastPageFailed
      ) {
        return
      }

      if (verticalScrollDirection === 'forward') {
        fetchNextPage()
      }
    },
    100,
  )

  const renderSubscriberItem: ComponentType<
    GridChildComponentProps<ListUser[]>
  > = useCallback(
    ({ data, rowIndex, columnIndex, style }) => {
      const itemIndex = rowIndex * 2 + columnIndex
      const subscriber = data[itemIndex]

      if (!subscriber) return null
      lastVisibleIndexRef.current = itemIndex

      return (
        <div
          style={style}
          className={classNames({
            'pr-2': columnIndex === 0,
            'pl-2': columnIndex === 1,
            'pb-4': rowIndex !== data.length / 2 - 1,
          })}
        >
          <UserCard
            isSelected={selectedUsers.some((u) => u.id === subscriber.id)}
            user={subscriber}
            onSelectedUsersChange={setSelectedUsers}
          />
        </div>
      )
    },
    [selectedUsers],
  )

  const resetSelection = () => setSelectedUsers([])

  return (
    <div className="relative flex h-full max-h-full max-w-full flex-col gap-4 overflow-hidden">
      <div className="flex gap-3">
        <IconButton
          icon={ChevronDownIcon}
          className="rotate-90"
          onClick={onFinish}
        />
        <p className="font-medium">Add User to {unescapeHtmlText(list.name)}</p>
      </div>
      <div className="flex items-center justify-end gap-3">
        <TextField
          placeholder="Search"
          className="dark:border-secondary-400 max-w-[216px] flex-1 resize-none"
          prefix={<SearchIcon className="text-title" />}
          onChange={handleSearchChange}
          defaultValue={query}
        />
        <UsersListFilters
          filters={filters}
          setFilters={setFilters}
          includeSignedUpFilters={false}
        />
      </div>
      <div
        className="max-w-full flex-1 overflow-hidden relative"
        ref={parentRef}
      >
        {isError && !isLastPageFailed && (
          <div>
            <p className="text-center">
              Something went wrong while loading users
            </p>
            <Button
              className="mt-2 mx-auto block"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </div>
        )}
        {isLastPageFailed && (
          <IconButton
            rounded
            icon={ReloadIcon}
            onClick={() => fetchNextPage()}
            className="absolute left-1/2 z-10 -translate-x-1/2 bg-white bottom-2 -rotate-90 p-2 rounded-full bg-card border dark:border-secondary-400"
          />
        )}
        {isLoading || !height || !width ? (
          <div className="grid grid-cols-2 gap-4">
            {times(4).map((num) => (
              <UserCardSkeleton key={num} />
            ))}
          </div>
        ) : !isEmpty(subscribers) ? (
          <VariableSizeGrid
            onScroll={handleScroll}
            className="scrollbar-hide"
            itemData={subscribers}
            rowHeight={() => 116}
            columnWidth={() => width / 2}
            key={width}
            height={height}
            rowCount={Math.ceil(subscribers.length / 2)}
            columnCount={2}
            width={width}
          >
            {renderSubscriberItem}
          </VariableSizeGrid>
        ) : (
          <div className="flex h-full flex-col items-center justify-center space-y-4">
            <NotFoundImage
              width={80}
              height={80}
            />
            <div className="text-secondary-300 text-sm leading-6">
              No users to add
            </div>
          </div>
        )}
      </div>
      <ManageSelectedUsers
        className="absolute bottom-0 left-0 right-0"
        onAdd={handleAdd}
        isAdding={addUsersMutation.isLoading}
        onCancel={resetSelection}
        selectedCount={selectedUsers.length}
      />
      {isLoadingMore && (
        <div className="bg-card absolute bottom-0 left-0 right-0 pt-3">
          <IconLoading className="mx-auto animate-spin" />
        </div>
      )}
    </div>
  )
}
